import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2, RotateCcw } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

type Env = "production" | "staging" | "development";

/**
 * Rollback button — atomic alias swap to a `ready` deployment. Used in
 * two places:
 *   1. Project Overview "Live now" card → rolls back to the immediately
 *      prior `ready` deployment in the same env (`targetDeploymentId`
 *      passed in by the parent).
 *   2. Deployment detail page → re-pins to THAT deployment specifically
 *      (operator chose a known-good prior artifact from the list).
 *
 * The rollback endpoint returns the new audit_event id; on success we
 * invalidate the deployments + project queries so the UI re-renders with
 * the new "active" flag set.
 */
export function RollbackButton({
  orgSlug,
  projectSlug,
  env,
  targetDeploymentId,
  size = "sm",
  variant = "default",
  label = "Rollback",
  disabled = false,
  onSuccess,
}: {
  orgSlug: string;
  projectSlug: string;
  env: Env;
  targetDeploymentId: string;
  size?: "sm" | "default";
  variant?: "default" | "primary" | "ghost";
  label?: string;
  disabled?: boolean;
  onSuccess?: (newDeploymentId: string) => void;
}) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const rollback = useMutation({
    mutationFn: () =>
      api.post<{ deploymentId: string }>(`/orgs/${orgSlug}/projects/${projectSlug}/rollback`, {
        deploymentId: targetDeploymentId,
        env,
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["deployments", orgSlug, projectSlug] });
      qc.invalidateQueries({ queryKey: ["project", orgSlug, projectSlug] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      setOpen(false);
      if (onSuccess) onSuccess(r.deploymentId);
      else nav(`/orgs/${orgSlug}/projects/${projectSlug}/deployments/${r.deploymentId}`);
    },
  });

  return (
    <>
      <Button
        variant={variant}
        size={size}
        disabled={disabled || rollback.isPending}
        onClick={() => setOpen(true)}
      >
        {rollback.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
        {label}
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="of-card w-[calc(100vw-2rem)] max-w-sm max-h-[85vh] overflow-y-auto">
            <div className="of-card-header"><h3 className="text-sm font-semibold">Roll back {env}?</h3></div>
            <div className="px-4 py-3 space-y-2">
              <p className="text-sm">
                This will atomically re-route <span className="font-mono">{env}</span> to the chosen prior
                deployment. No new build runs; traffic switches within seconds.
              </p>
              <p className="text-xs text-muted-foreground">
                Env vars are NOT rolled back. If they changed since this artifact was built, the rolled-back
                deployment may behave differently than when it originally shipped.
              </p>
              {rollback.error instanceof ApiError && (
                <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">
                  {rollback.error.message}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="default" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={() => rollback.mutate()} disabled={rollback.isPending}>
                  {rollback.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Confirm rollback
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
